import React from 'react';
import { format } from 'date-fns';
import { range } from 'lodash-es';

import {
  Box,
  DateInput,
  DatePicker,
  Divider,
  FormField,
  FormHelp,
  Heading,
  Input,
  Label,
  Text,
  TextButton,
} from '@zillow/constellation-10';
import { IconPlusCircleFilled } from '@zillow/constellation-icons';

import type { Section } from '../../../models/index';
import { Field, VaporIntrusionSection } from '../../../models/index';
import { VAPOR_INTRUSION_ZONE } from '../../../constants/index';
import { DATE_PICKER_FORMAT } from '../../../constants/stringConstants';
import YesAndNo from '../../Widgets/YesAndNo';
import { useIndexedSection } from '../../../hooks';

export const MAXIMUM_REPORTS = 3;

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
  setSection: (section: VaporIntrusionSection) => void;
};

export default function VaporIntrusionZone({ section, setSectionValues, setSection }: Props): JSX.Element {
  const { onAddOne, onDeleteOne } = useIndexedSection({
    section,
    setSection,
    SectionClass: VaporIntrusionSection,
  });

  function onRemoveAll(): void {
    const updatedSection = section.clone();
    updatedSection.removeAllIndexes();
    setSection(updatedSection);
  }

  function setName(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { index } = e.currentTarget.dataset;
    const fieldName = Field.getIndexedFieldName(VAPOR_INTRUSION_ZONE.FIELD_NAME.REPORT_NAME, Number(index));

    setSectionValues({ [fieldName]: e.currentTarget.value });
  }

  function setNumber(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { index } = e.currentTarget.dataset;
    const fieldName = Field.getIndexedFieldName(VAPOR_INTRUSION_ZONE.FIELD_NAME.REPORT_NUMBER, Number(index));

    setSectionValues({ [fieldName]: e.currentTarget.value });
  }

  function setDate(index: number, formattedDate: string): void {
    const fieldName = Field.getIndexedFieldName(VAPOR_INTRUSION_ZONE.FIELD_NAME.REPORT_DATE, Number(index));

    setSectionValues({ [fieldName]: formattedDate });
  }

  function onYesVaporIntrusionZone(): void {
    setSectionValues({
      [VAPOR_INTRUSION_ZONE.FIELD_NAME.IS_ENABLED]: true,
    });
  }

  function onNoVaporIntrusionZone(): void {
    setSectionValues({
      [VAPOR_INTRUSION_ZONE.FIELD_NAME.IS_ENABLED]: false,
    });
    onRemoveAll();
  }

  function onYesHasBeenTested(): void {
    setSectionValues({
      [VAPOR_INTRUSION_ZONE.FIELD_NAME.HAS_BEEN_TESTED]: true,
    });
    onAddOne();
  }

  function onNoHasBeenTested(): void {
    setSectionValues({
      [VAPOR_INTRUSION_ZONE.FIELD_NAME.HAS_BEEN_TESTED]: false,
    });
    onRemoveAll();
  }

  function renderReport(index: number): React.ReactNode {
    const nameField = section.getField(Field.getIndexedFieldName(VAPOR_INTRUSION_ZONE.FIELD_NAME.REPORT_NAME, index));
    const numberField = section.getField(
      Field.getIndexedFieldName(VAPOR_INTRUSION_ZONE.FIELD_NAME.REPORT_NUMBER, index),
    );
    const dateField = section.getField(Field.getIndexedFieldName(VAPOR_INTRUSION_ZONE.FIELD_NAME.REPORT_DATE, index));

    return (
      <>
        <Box css={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'layout.tighter' }}>
          <Heading level={4} textStyle="responsive.heading-xs" id={`vapor-intrusion-report-#${index + 1}`}>
            Report #{index + 1}
          </Heading>
          {index > 0 && (
            <TextButton data-index={index} data-test="delete-vapor-intrusion-report" onClick={onDeleteOne}>
              Delete
            </TextButton>
          )}
        </Box>
        <FormField
          required
          css={{ marginBottom: 'layout.tight' }}
          label={<Label>Name</Label>}
          control={<Input data-index={index} value={nameField?.enteredValue} onChange={setName} />}
        />
        <FormField
          required
          css={{ marginBottom: 'layout.tight' }}
          label={<Label>Number</Label>}
          control={<Input data-index={index} value={numberField?.enteredValue} onChange={setNumber} />}
        />
        <FormField
          required
          css={{ marginBottom: 'layout.tight' }}
          label={<Label>Date</Label>}
          control={
            <DatePicker
              dateFormat={DATE_PICKER_FORMAT}
              value={dateField?.enteredValue}
              onValueChange={(value) => {
                const formatted = typeof value === 'string' ? value : format(value, DATE_PICKER_FORMAT);
                setDate(index, formatted);
              }}
              maxDate={new Date()}
              closeModalOnDateSelect
              input={<DateInput />}
            />
          }
        />
      </>
    );
  }

  const totalVaporIntrusionZones = section.getTotalIndexes();
  if (totalVaporIntrusionZones == null) {
    return null;
  }

  const vaporIntrusionZonesEnabled = section.getFieldValue(VAPOR_INTRUSION_ZONE.FIELD_NAME.IS_ENABLED);
  const hasBeenTestedEnabled = section.getFieldValue(VAPOR_INTRUSION_ZONE.FIELD_NAME.HAS_BEEN_TESTED);

  return (
    <>
      <FormField
        required
        css={{ marginBottom: 'layout.tight' }}
        label={<Label>Is the rental property within the Southeast Como Trichloroethylene Vapor Intrusion Zone?</Label>}
        control={
          <YesAndNo
            yesHandler={onYesVaporIntrusionZone}
            noHandler={onNoVaporIntrusionZone}
            yesActive={vaporIntrusionZonesEnabled}
          />
        }
      />
      {vaporIntrusionZonesEnabled && (
        <FormField
          required
          css={{ marginBottom: 'layout.tight' }}
          label={
            <Label>
              Has the US EPA, MPCA or other related or appropriate governmental agency tested for environmental
              contamination at this property?
            </Label>
          }
          control={
            <YesAndNo
              yesHandler={onYesHasBeenTested}
              noHandler={onNoHasBeenTested}
              yesActive={section.getFieldValue(VAPOR_INTRUSION_ZONE.FIELD_NAME.HAS_BEEN_TESTED)}
            />
          }
          description={
            <FormHelp>
              You must provide copies of any reports before the lease is signed by tenant. Later on, you will have the
              ability to attach any reports to your lease.
            </FormHelp>
          }
        />
      )}
      {hasBeenTestedEnabled && (
        <>
          {range(0, totalVaporIntrusionZones).map((index: number) => (
            <React.Fragment key={index}>
              {index > 0 && <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />}
              {renderReport(index)}
            </React.Fragment>
          ))}
          {totalVaporIntrusionZones < MAXIMUM_REPORTS ? (
            totalVaporIntrusionZones >= 0 && (
              <TextButton onClick={onAddOne} icon={<IconPlusCircleFilled />} data-test="add-another">
                Add another report
              </TextButton>
            )
          ) : (
            <Text data-test="maximum-reached">
              You’ve added {MAXIMUM_REPORTS} reports, which is the maximum allowed.
            </Text>
          )}
        </>
      )}
    </>
  );
}
